import { Avatar, Button, Skeleton } from "@mui/material";
import { DateTime } from "luxon";
import { JSXElementConstructor, createElement } from "react";
import { IconType } from "react-icons";
import { TbArrowMoveRight, TbCalendar, TbHeartHandshake, TbPlane, TbPlaneInflight } from "react-icons/tb";
import { NavLink } from "react-router-dom";

import { SoftColor } from "@/constants/colors";
import { formatFlightNumber } from "@/helpers/strings";
import { useMetasActions } from "@/stores/use-metas";
import { IPlanPassenger } from "@/types/tasks.types";

import PassengerStatus, { PassengerStatusSkeleton } from "../PassengerStatus";
import HighlightText from "./HighlightText";

const classes = {
    wrapper: "flex flex-col gap-1 pb-8 relative group",
    titleContent:
        "flex justify-center border-b group-hover:border-b-indigo-200 py-2 items-center gap-2 text-xs font-semibold text-gray-700",
    rowWrapper: "grid grid-cols-[25px,1fr,1fr] items-center gap-1 xl:gap-2 text-xs font-semibold text-gray-600",
};

export type GlobalSearchDirectionItem = {
    renderValue: JSXElementConstructor<GlobalSearchDirectionItemProps>;
    label: string;
    icon: IconType;
    field: keyof IPlanPassenger<"db">;
};

export type GlobalSearchDirectionItemProps = {
    passenger: IPlanPassenger<"db">;
    field: keyof IPlanPassenger<"db">;
    highlightFlightNumber?: string;
};
function GlobalSearchDirectionDateItem({ passenger, field }: GlobalSearchDirectionItemProps) {
    const fieldValue = passenger[field];
    const value = fieldValue && typeof fieldValue === "string" ? fieldValue : null;
    return (
        (value && DateTime.fromISO(value).setZone("Europe/Istanbul").setLocale("tr").toFormat("d LLL HH:mm")) || (
            <span>-</span>
        )
    );
}
function GlobalSearchDirectionFlightNumberItem({
    passenger,
    field,
    highlightFlightNumber,
}: GlobalSearchDirectionItemProps) {
    const fieldValue = passenger[field];
    const value = fieldValue && typeof fieldValue === "string" ? fieldValue : null;
    if (!value) return <>-</>;

    const formatted = formatFlightNumber(value);
    return highlightFlightNumber ? (
        <HighlightText text={formatted} highlight={highlightFlightNumber} />
    ) : (
        <>{formatted}</>
    );
}
const globalSearchDirectionItems: GlobalSearchDirectionItem[] = [
    {
        renderValue: GlobalSearchDirectionDateItem,
        label: "Alım Saati",
        icon: TbHeartHandshake,
        field: "pickUp",
    },
    {
        renderValue: GlobalSearchDirectionDateItem,
        label: "Uçak Saati",
        icon: TbPlane,
        field: "flightTime",
    },
    {
        renderValue: GlobalSearchDirectionFlightNumberItem,
        label: "Uçak Kodu",
        icon: TbPlaneInflight,
        field: "flightNumber",
    },
];

function GlobalSearchDirectionRowSkeleton() {
    return (
        <div className={classes.rowWrapper}>
            <Skeleton variant="rounded" width={22} height={22} />
            <Skeleton variant="text" width={75} height={20} />
            <span className="ml-auto text-right">
                <Skeleton variant="text" width={75} height={20} />
            </span>
        </div>
    );
}
function GlobalSearchDirectionRow({
    color,
    passenger,
    label,
    icon,
    renderValue,
    field,
    highlightFlightNumber,
}: GlobalSearchDirectionItem & { color: SoftColor; passenger: IPlanPassenger<"db">; highlightFlightNumber?: string }) {
    return (
        <div className={classes.rowWrapper}>
            <Avatar variant="rounded" sx={{ width: 22, height: 22, backgroundColor: color.bg, color: color.text }}>
                {createElement(icon, { size: 14 })}
            </Avatar>
            {label}:
            <span className="ml-auto text-right">
                {createElement(renderValue, { passenger, field, highlightFlightNumber })}
            </span>
        </div>
    );
}

export function GlobalSearchPassengerDirectionItemSkeleton() {
    return (
        <div className={classes.wrapper}>
            <div className={classes.titleContent}>
                <Skeleton variant="rounded" width={22} height={22} />
                <Skeleton variant="text" width={75} height={20} />
            </div>
            <div className=" flex justify-center">
                <PassengerStatusSkeleton />
            </div>
            <div className=" -mx-2 grid items-center font-semibold px-2 py-1 rounded text-xs   gap-1  grid-cols-[1fr,30px,1fr] bg-gray-100">
                <Skeleton variant="text" width={75} height={20} />
                <TbArrowMoveRight
                    fontSize={20}
                    color="grey"
                    className={"!shrink-0 !self-center duration-200 mx-auto "}
                />
                <span className="ml-auto">
                    <Skeleton variant="text" width={75} height={20} />
                </span>
            </div>
            {globalSearchDirectionItems.map((_, index) => (
                <GlobalSearchDirectionRowSkeleton key={index} />
            ))}
        </div>
    );
}

function GlobalSearchPassengerDirectionItem({
    color,
    titleIcon,
    title,
    passenger,
    highlightFlightNumber,
}: {
    color: SoftColor;
    titleIcon: IconType;
    title: string;
    passenger?: IPlanPassenger<"db">;
    highlightFlightNumber?: string;
}) {
    const { setShowGlobalSearch } = useMetasActions();
    if (!passenger) return <div className=" grid place-content-center">Bilgi Bulunamadı</div>;
    return (
        <div className={classes.wrapper}>
            <Button
                component={NavLink}
                to={`/plan?searchItemId=${passenger.id}`}
                className="!absolute !bottom-0 !w-full  !opacity-0 !translate-y-[100px] group-hover:!translate-y-0 !transition-ease-in-out  !z-50 group-hover:!opacity-100 !transition-all  !duration-200"
                variant="text"
                startIcon={<TbCalendar />}
                onClick={() => setShowGlobalSearch(false)}
                color="secondary"
                size="small"
            >
                Tabloda Görüntüle
            </Button>

            <div className={classes.titleContent}>
                <Avatar variant="rounded" sx={{ width: 22, height: 22, backgroundColor: color.bg, color: color.text }}>
                    {createElement(titleIcon, { color: color.text, size: 16 })}
                </Avatar>
                {title}
            </div>

            {passenger.status && (
                <div className=" flex justify-center">
                    <PassengerStatus size="small" status={passenger.status} color={color} />
                </div>
            )}
            <div className=" -mx-2 grid items-center font-semibold px-2 py-1 rounded text-xs   gap-1  grid-cols-[1fr,30px,1fr] bg-gray-100">
                {passenger.origin.alias || "-"}
                <TbArrowMoveRight fontSize={20} className={"!shrink-0 !self-center duration-200 mx-auto "} />
                <span className="ml-auto">{passenger.destination.alias || "-"}</span>
            </div>

            {globalSearchDirectionItems.map((item) => (
                <GlobalSearchDirectionRow
                    key={item.label}
                    color={color}
                    passenger={passenger}
                    highlightFlightNumber={highlightFlightNumber}
                    {...item}
                />
            ))}
        </div>
    );
}

export default GlobalSearchPassengerDirectionItem;
